import { useCallback } from 'react';
import useAxiosInstance from './axiosInstance';
import JSZip from 'jszip';

/**
 * Custom hook to download files from the server.
 * @param {function} navigate - Function to navigate to another page.
 * @returns {object} Object containing downloadFile and downloadFiles functions.
 */
const useFileDownload = (navigate, container_name=null) => {
  const axiosInstance = useAxiosInstance(navigate);

  const downloadFile = useCallback(async (fileName) => {
    try {
      const response = await axiosInstance.post(container_name ? `/files/sas?container=${container_name}` : '/files/sas', {
        fileName, // Pass the file name in the request body
      });
      const anchor = document.createElement('a');
      anchor.href = response.data.sas_url; // File URL
      anchor.download = fileName; // Name of the downloaded file
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor); // Cleanup
    } catch (error) {
      console.error(`Error downloading file: ${fileName}`, error);
      throw error;
    }
  }, [axiosInstance, container_name]);

  const downloadFiles = useCallback(async (files, onProgress, batchSize = 5) => {
    const totalFiles = files.length;
    let downloadedFiles = 0;
    const zip = new JSZip(); // Create a new JSZip instance

    const updateProgress = () => {
      const percentCompleted = Math.round((downloadedFiles / totalFiles) * 100);
      if (onProgress) onProgress(percentCompleted);
    };

    const downloadBatch = async (batch) => {
      await Promise.all(
        batch.map(async (file) => {
          try {
            const response = await axiosInstance.post('/download', { fileName: file }, { responseType: 'blob' });
            const arrayBuffer = await response.data.arrayBuffer();
            zip.file(file, arrayBuffer);

            downloadedFiles += 1;
            updateProgress();
          } catch (error) {
            console.error(`Error downloading file: ${file}`, error);
          }
        })
      );
    };

    for (let i = 0; i < files.length; i += batchSize) {
      const batch = files.slice(i, i + batchSize);
      await downloadBatch(batch);
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const anchor = document.createElement('a');
    const zipUrl = URL.createObjectURL(zipBlob);
    anchor.href = zipUrl;
    anchor.download = 'downloaded_files.zip'; // Name of the zip file
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor); // Cleanup
    URL.revokeObjectURL(zipUrl); // Release the blob URL

    console.log('All files downloaded and zipped successfully.');
  }, [axiosInstance]);

  return { downloadFile, downloadFiles };
};

export default useFileDownload;